<template>
  <b-container fluid class="physical-chemical-report-print">
    <div class="physical-chemical-report-print__title">
      <strong>
        {{ $t('engineering.physical-chemical.print.title', { screenName }) }}
      </strong>
    </div>
    <s-loader
      v-if="loading"
      class="mt-2"
      size="lg"
    />
    <div v-else>
      <div class="physical-chemical-report-print__header">
        <div class="physical-chemical-report-print__sample-infos">
        <span class="physical-chemical-report-print__sample-oil">
          <strong>
            {{ $t('engineering.reports.sample') }}:
          </strong>
          <span>
            {{ reportInfos.sampleNumber }}
          </span>
          <span v-show="reportInfos.processingPending">
            - {{ $t('general.attention') }}:
            {{ $t('engineering.reports.processing-pending-sample') }}
          </span>
          <strong class="ml-4">
            {{ $t('engineering.reports.sample-type') }}:
          </strong>
          {{ reportInfos.sampleType }}
        </span>
          <span class="physical-chemical-report-print__sample-date">
          {{ sampleDateTime | dateTimeFilter({ timeZone: timezones.UTC }) }}
        </span>
        </div>
        <summary-transformer-info
          :loading.sync="loadingSummary"
        />
      </div>
      <div v-for="rule in dataToShow.byRules" :key="rule.norm">
        <component
          :is="tableComponent"
          :rule="rule"
          :table="dataToShow"
          :items="testsFields"
        />
      </div>
    </div>
  </b-container>
</template>

<script>
import SummaryTransformerInfo from '@/domain/components/installation/supervision/SummaryTransformerInfo';
import ReportPrintTable from '@/domain/components/installation/engineering/physicalChemical/print/ReportPrintTable';
import Service from '@/domain/services/installation/engineering/physicalChemical/generalTab';
import { sampleType } from '@/domain/enums/sample';
import ParametersPrintTable from '@/domain/components/installation/engineering/physicalChemical/print/ParametersPrintTable';
import LoadingMixin from '@/services/loadingMixin';
import print from '@/services/print';
import TestsFields
  from '@/domain/data/installation/engineering/physicalChemical/print/lots-and-parameters-print-tests-fields.json';
import { timezones } from '@/constants/localizer';

export default {
  name: 'PhysicalChemicalReportPrint',
  components: { ParametersPrintTable, SummaryTransformerInfo, ReportPrintTable },
  mixins: [LoadingMixin],
  data: () => ({
    timezones,
    reportInfos: {},
    loadingSummary: false,
  }),
  computed: {
    sampleDateTime() {
      return this.reportInfos?.reports?.tests?.sampleDateTime;
    },
    tableComponent() {
      return this.isParameters ? ParametersPrintTable : ReportPrintTable;
    },
    screenName() {
      return this.isParameters
        ? this.$t('engineering.physical-chemical.print.parameter-screen')
        : this.$t('engineering.physical-chemical.print.report-screen');
    },
    isParameters() {
      return (this.$route.query || {}).parameters === 'true';
    },
    alarmedOnly() {
      return (this.$route.query || {}).alarmedOnly === 'true';
    },
    dataToShow() {
      const dataToShow = this.isParameters
        ? this.reportInfos.parameters
        : this.reportInfos.reports;

      if (!this.alarmedOnly) {
        return dataToShow;
      }

      return {
        ...dataToShow,
        byRules: dataToShow.byRules
        .filter(({ rules }) => rules.some(({ isAlarmed }) => isAlarmed)),
      };
    },
    testsFields() {
      return this.isParameters
        ? this.parametersFieldsToShow
        : this.reportFieldsToShow;
    },
    parametersFieldsToShow() {
      if (!this.alarmedOnly) {
        return TestsFields.parametersTestsFields;
      }
      return TestsFields.parametersTestsFields.filter(field => this.reportInfos.parameters
        .byRules.some(({ rules }) => rules
        .some(({ fieldsWithDiagnosis }) => fieldsWithDiagnosis.includes(field.key))));
    },
    reportFieldsToShow() {
      if (!this.alarmedOnly) {
        return TestsFields.lotsTestsFields;
      }
      return TestsFields.lotsTestsFields.filter(field => this.reportInfos.reports
        .byRules.some(({ rules }) => rules
        .some(rule => rule[field.key] != null)));
    },
  },
  created() {
    this.getTableItems();
  },
  methods: {
    getTableItems() {
      this.startLoading();
      const { assetModuleId, sampleId } = this.$route.params;
      Service.getSampleTables(assetModuleId, sampleId)
        .then((response) => {
          this.reportInfos = response;
          this.reportInfos.sampleType = this.getSampleType(response.reports);
          this.reportInfos.sampleNumber = response.sampleNumber || '--';
          this.reportInfos.processingPending = response.processingPending;
          this.callPrint();
        })
        .finally(() => {
          this.endLoading();
        });
    },
    getSampleType(reports = {}) {
      if (!reports.tests) return '';
      const sampleTypeLabel = {
        [sampleType.USED]: this.$t('enums.sample-type.used'),
        [sampleType.NEW]: this.$t('enums.sample-type.new'),
        [sampleType.RECONDICIONED]: this.$t('enums.sample-type.recondicioned'),
        [sampleType.REGENERATED]: this.$t('enums.sample-type.regenerated'),
      };
      return sampleTypeLabel[reports.tests.sampleType];
    },
    callPrint() {
      if (!this.loading && !this.loadingSummary) {
        setTimeout(() => {
          print();
        }, 1000);
      }
    },
  },
  watch: {
    loadingSummary() {
      this.callPrint();
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .physical-chemical-report-print
    padding: 0 25px
    &__header
      margin-bottom: 20px
    &__title
      font-size: 1.2rem
      padding: 10px
      text-align: center
    &__sample-oil
      font-size: 1.2rem
    &__sample-infos
      display: flex
      justify-content: space-between
  @media print
    .physical-chemical-report-print
      font-family: "Trebuchet MS", Helvetica, sans-serif
      ::v-deep
        tr td div
          page-break-inside: avoid
        table.table thead th
          background-color: var(--color-table-header-bg) !important
        .summary-transformer-info
          padding: 5px 10px 2px
  @page
    size: A4
    margin: 9mm 0
</style>
